<template>
	<div v-if="show" class="laic-chat" data-testid="laic-chat">
		<button aria-label="Open Chat" v-if="!isOpen" class="icon" data-testid="btn-open" @click="open">
			<ChatIcon />
		</button>
		<div v-if="isOpen" class="wrapper modal" data-testid="chat-wrapper">
			<div class="modal-header">
				<button
					type="button"
					class="btn-close"
					aria-label="Close Chat"
					data-testid="btn-close"
					@click="close()"
				></button>
			</div>
			<LaicForm v-if="step === 'form'" :user-data="userData" @form-submitted="handleForm" />
			<LaicTellUsMore v-else-if="step === 'tellUsMore'" @step-change="handleStepEmit" />
			<LaicMessages v-else-if="step === 'chat'" :user-data="userData!" />
		</div>
	</div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { onMounted, ref, type Ref } from 'vue';
import ChatIcon from '@/lib/icons/ChatIcon.vue';
import LaicForm from '@/lib/components/laic-chat/LaicForm.vue';
import LaicTellUsMore from '@/lib/components/laic-chat/LaicTellUsMore.vue';
import LaicMessages from '@/lib/components/laic-chat/LaicMessages.vue';
import { getAuthoredContent } from '@/util/getAuthoredContent';
import type {
	LaicChatAuthoredContent,
	LaicChatContentFragment,
	UserData
} from 'Types/aemui/laic-chat/laicChat';
import { logError } from '@/util/logger';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

const validateTimeRange = () => {
	const days = authoredContent.value?.master;
	if (!days) {
		logError(`No Content Fragment for LAIC Chat schedule`, { content: authoredContent.value });
		return;
	}

	const today = dayjs()
		.tz('America/Chicago')
		.format('ddd')
		.toLowerCase() as keyof LaicChatContentFragment;
	if (!(today in days)) {
		logError(`Missing day in LAIC Chat schedule ERROR for ${today}`, {
			schedule: authoredContent.value?.master
		});
		return;
	}

	const blocksJSON = days[today];
	if (typeof blocksJSON !== 'string') {
		logError(`Missing time range in LAIC Chat schedule ERROR for ${today}`, {
			schedule: authoredContent.value?.master
		});
		return;
	}

	let blocks: string[] = [];
	try {
		const parsed = JSON.parse(blocksJSON);
		if (!Array.isArray(parsed)) {
			throw new TypeError('blocks are not an array');
		}
		blocks = parsed;
	} catch {
		logError(`Parse LAIC blocksJSON ERROR for ${blocksJSON}`, {
			schedule: authoredContent.value?.master
		});
	}
	return blocks.map((block) => {
		if (typeof block !== 'string') {
			logError(`Parse LAIC schedule for ${today}. Individual range isn't a string`, {
				schedule: authoredContent.value?.master
			});
			return null;
		}
		// Captures start/end hours and minutes from a 24-hour "HH:MM-HH:MM" range string
		const matches = block.match(/^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/);
		if (!matches) {
			logError(`Parse LAIC schedule ERROR for ${today}`, {
				schedule: authoredContent.value?.master
			});
			return null;
		}
		return {
			startHour: Number.parseInt(matches[1]!, 10),
			startMin: Number.parseInt(matches[2]!, 10),
			endHour: Number.parseInt(matches[3]!, 10),
			endMin: Number.parseInt(matches[4]!, 10)
		};
	});
};

const checkTimes = () => {
	const times = validateTimeRange();
	if (!times) {
		return false;
	}
	return times.some((time) => {
		if (!time) {
			return false;
		}
		const { startHour, startMin, endHour, endMin } = time;
		const now = dayjs().tz('America/Chicago');
		const start = now.hour(startHour).minute(startMin).second(0).millisecond(0);
		const end = now.hour(endHour).minute(endMin).second(0).millisecond(0);
		return now.isBetween(start, end, null, '[)');
	});
};

const show = ref(false);
const authoredContent: Ref<LaicChatAuthoredContent | null> = ref(null);
onMounted(() => {
	authoredContent.value = getAuthoredContent<LaicChatAuthoredContent>('aemuiLaicChatScript');
	show.value = window.lt.pageProperties.experienceType === 'prospect' && checkTimes();
});

const isOpen = ref(false);

const close = () => {
	isOpen.value = false;
	step.value = 'form';
	userData.value = null;
};

const open = () => {
	isOpen.value = true;
};

type IStep = 'form' | 'tellUsMore' | 'chat';
const step: Ref<IStep> = ref('form');

const userData: Ref<UserData | null> = ref(null);

const handleForm = (formValues: UserData) => {
	userData.value = formValues;
	step.value = 'tellUsMore';
};

const handleStepEmit = (emittedStep: IStep) => {
	step.value = emittedStep;
};

defineExpose({
	step,
	userData,

	handleForm
});
</script>

<style lang="scss" scoped>
.laic-chat {
	--kpl-btn-active-color: var(--kpl-off-white);
	--kpl-btn-active-bg: var(--kpl-dark);

	position: fixed;
	bottom: 16px;
	left: 16px;
	z-index: 1040;

	.icon {
		background-color: #e0e0e0;
		border-radius: 50%;
		border: 0;
		box-shadow: rgba(0, 0, 0, 0.2) 2px 4px 2px;
		height: 48px;
		width: 48px;
		padding: 0;
		cursor: pointer;

		svg {
			width: 36px;
			height: 36px;
			color: var(--kpl-dark);
		}
	}

	:deep(.wrapper) {
		display: flex;
		flex-direction: column;
		position: absolute;
		bottom: -16px;
		left: -16px;
		top: auto;
		height: 100vh;
		width: 100vw;
		overflow: hidden;
		@include media-breakpoint-up(sm) {
			bottom: 0;
			left: 0;
			height: 576px;
			width: 320px;
		}
		border: solid 1px var(--kpl-border-color-translucent);
		background-color: var(--kpl-body-bg);
		backdrop-filter: blur(32px);
		box-shadow: rgba(0, 0, 0, 0.3) 0 0 20px;

		.modal-header,
		.modal-footer {
			border-color: var(--kpl-border-color-translucent);
		}

		.modal-body {
			overflow-y: auto;
		}
	}
}
</style>
